import { Helmet } from 'react-helmet-async';

/**
 * Enhanced SEO component with structured data support
 *
 * @param {string} title - Page title (max ~60 chars)
 * @param {string} description - Meta description (140–160 chars optimal)
 * @param {string} path - Path beginning with /
 * @param {string} type - OG type (website | article | product)
 * @param {string} image - Absolute URL for og:image (1200x630 recommended)
 * @param {string[]} keywords - Array of focus keywords
 * @param {object|object[]} jsonLd - One or more JSON-LD schema objects
 */
const SEO = ({
  title,
  description,
  path,
  type = 'website',
  image,
  keywords = [],
  jsonLd,
}) => {
  const siteName = 'SOGNUDIMARE — Croisières Catamaran Corse, Sardaigne & Méditerranée';
  const baseUrl = 'https://sognudimare.com';
  const fullTitle = title ? `${title} | SOGNUDIMARE` : siteName;
  const url = path ? `${baseUrl}${path}` : baseUrl;
  const ogImage =
    image ||
    'https://static.wixstatic.com/media/ce6ce7_a82e3e86741143d6ab7acd99c121af7b~mv2.png/v1/fill/w_1200,h_630,al_c,q_85,enc_auto/croisieres%20catamaran%20corse%20sognudimare.png';

  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  // Always include the Organization schema
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    name: 'SOGNUDIMARE',
    alternateName: 'Sognu di Mare',
    url: baseUrl,
    logo: ogImage,
    image: ogImage,
    description:
      'Spécialiste de la croisière catamaran en pension complète avec équipage en Corse, Sardaigne, Grèce, Cyclades et Caraïbes. Réservation à la cabine ou privatisation.',
    telephone: '+33-4-95-72-90-28',
    email: 'contact@sognudimare-catamarans.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Port Tino Rossi',
      addressLocality: 'Ajaccio',
      postalCode: '20000',
      addressRegion: 'Corse',
      addressCountry: 'FR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 41.917,
      longitude: 8.738,
    },
    areaServed: [
      { '@type': 'Place', name: 'Corse' },
      { '@type': 'Place', name: 'Sardaigne' },
      { '@type': 'Place', name: 'Grèce' },
      { '@type': 'Place', name: 'Cyclades' },
      { '@type': 'Place', name: 'Méditerranée' },
      { '@type': 'Place', name: 'Caraïbes' },
    ],
    sameAs: [
      'https://www.instagram.com/sognudimare/',
      'https://www.facebook.com/sognudimare',
    ],
    priceRange: '€€€',
    knowsAbout: [
      'croisière catamaran',
      'croisière catamaran corse',
      'croisière catamaran avec équipage',
      'croisière catamaran pension complète',
      'privatisation catamaran',
      'tour de corse en catamaran',
      'slow tourisme',
    ],
  };

  schemas.unshift(orgSchema);

  return (
    <Helmet>
      <html lang="fr" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <link rel="canonical" href={url} />

      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="SOGNUDIMARE" />
      <meta name="geo.region" content="FR-COR" />
      <meta name="geo.placename" content="Ajaccio, Corse-du-Sud" />
      <meta name="geo.position" content="41.917;8.738" />
      <meta name="ICBM" content="41.917, 8.738" />

      {/* Open Graph */}
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="SOGNUDIMARE" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title || siteName} — Croisière catamaran SOGNUDIMARE`} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
